package io.horion.cv.pages.dashboard

import androidx.compose.runtime.*
import io.horion.cv.components.auth.RequireAuth
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronRight
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import io.horion.cv.api.datasource.common.Generate
import io.horion.cv.components.layouts.AppResponsiveLayout
import io.horion.cv.components.shared.CreateDocumentCard
import io.horion.cv.components.shared.DashboardCardBlock
import io.horion.cv.components.shared.DocumentCard
import io.horion.cv.di.koinGet
import io.horion.cv.pages.navigation.Screen
import io.horion.cv.styles.ChevronButtonIconStyle
import io.horion.cv.styles.DashboardGridStyle
import io.horion.cv.styles.ReAccordionPlusIconStyles
import io.horion.cv.styles.SpanTextStylecss
import io.horion.cv.styles.TextStyleCss
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import io.horion.cv.utils.Constants.UBUNTU
import io.horion.cv.viewModel.DashboardViewModel
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

@Page("tableau-de-bord")
@Composable
fun DashboardPage() = RequireAuth {

    val ctx = rememberPageContext()

    val dashboardViewModel = koinGet<DashboardViewModel>()

    LaunchedEffect(Unit) {
        dashboardViewModel.loadCvHistory(refresh = true)
        dashboardViewModel.loadLetterHistory(refresh = true)
    }

    AppResponsiveLayout(activeLabel = "Tableau de bord") {

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .gap(64.px) // Espacement vertical entre les rangées de sections
        ) {
            // Première rangée : CVs et Lettres de motivation
            Div(DashboardGridStyle.toModifier().toAttrs()) {
                DashboardSection(title = "Curriculum vitae") {
                    CvGrid(
                        dashboardViewModel.cvList,
                        onNew = { ctx.router.navigateTo(Screen.CvDashboard.path) },
                        onEdit = { generate -> ctx.router.navigateTo("${Screen.UpdateCv.path}?id=${generate.uuidDocument}&model=${generate.model ?: ""}") },
                        onDelete = { id -> dashboardViewModel.deleteCv(id) },
                        onDownload = { url -> dashboardViewModel.downloadCv(url) }
                    )
                }
                DashboardSection(title = "Lettres de motivation") {
                    LetterGrid(
                        dashboardViewModel.letterList,
                        onNew = { ctx.router.navigateTo(Screen.LetterDashboard.path) },
                        onEdit = { generate -> ctx.router.navigateTo("${Screen.UpdateLetter.path}?id=${generate.uuidDocument}&model=${generate.model ?: ""}") },
                        onDelete = { id -> dashboardViewModel.deleteLetter(id) },
                        onDownload = { url -> dashboardViewModel.downloadCv(url) }
                    )
                }
            }

            // Deuxième rangée : Offres et Candidatures
            Div(DashboardGridStyle.toModifier().toAttrs()) {
                DashboardSection(title = "Offres d'emplois") {
                    JobOffersBlock()
                }
                DashboardSection(title = "Candidatures") {
                    ApplicationsBlock { ctx.router.navigateTo(Screen.CandidaturesDashboard.path) }
                }
            }
        }
    }
}

@Composable
fun DashboardSection(
    title: String,
    content: @Composable () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .gap(40.px)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            SpanText(
                text = title,
                modifier = TextStyleCss.toModifier()
//                    .fontSize(if (isMobile) 16.px else 20.px)
                    .fontFamily(UBUNTU)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Colors.DarkBlue)
            )
            Box(
                modifier = Modifier
                    .size(35.px)
                    .border(1.px, LineStyle.Solid, Colors.GrayLight)
                    .borderRadius(6.px)
                    .cursor(Cursor.Pointer),
                contentAlignment = Alignment.Center
            ) {
             /*   Image(
                    src = "/chevron-right1.svg",
                    description = "Voir tout",
                    modifier = Modifier.size(20.px)
                )*/
                FaChevronRight(
                    modifier = Modifier.fontSize(20.px),
                    size = IconSize.SM
                )
            }
        }
        content()
    }
}

@Composable
fun CvGrid(
    cvList: List<Generate>,
    onNew: () -> Unit,
    onEdit: (Generate) -> Unit = {},
    onDelete: (String) -> Unit,
    onDownload: (String) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth().gap(16.px)
            .styleModifier { property("flex-wrap", "wrap") }
    ) {
        CreateDocumentCard(
            text = "Créer un nouveau CV",
            onClick = onNew
        )

        cvList.forEach { cv ->
            DocumentCard(
                title = cv.name ?: "CV sans titre",
                modifiedAgo = cv.date,
                previewSrc = cv.thumbnailUrl.ifBlank { null },
//                cardW = cardW, cardH = cardH,
                onEdit = { onEdit(cv) },
                onDelete = {
                    if (kotlinx.browser.window.confirm("Voulez-vous vraiment supprimer ce CV ?")) {
                        onDelete(cv.id)
                    }
                },
                onDownload = { onDownload(cv.url) }
            )
        }
    }
}

@Composable
fun LetterGrid(
    letterList: List<Generate>,
    onNew: () -> Unit,
    onEdit: (Generate) -> Unit = {},
    onDelete: (String) -> Unit,
    onDownload: (String) -> Unit
) {
    Row(
        modifier = Modifier.
        fillMaxWidth()
            .gap(16.px)
            .styleModifier { property("flex-wrap", "wrap") }
    ) {
        CreateDocumentCard(
            text = "Créer une nouvelle lettre \nde motivation",
            onClick = onNew
        )

        letterList.forEach { letter ->
            DocumentCard(
                title = letter.name ?: "Lettre sans titre",
                modifiedAgo = letter.date,
                previewSrc = letter.thumbnailUrl.ifBlank { null },
//                cardW = cardW, cardH = cardH,
                onEdit = { onEdit(letter) },
                onDelete = {
                    if (kotlinx.browser.window.confirm("Voulez-vous vraiment supprimer cette lettre ?")) {
                        onDelete(letter.id)
                    }
                },
                onDownload = { onDownload(letter.url) }
            )
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun JobOffersBlock() {
    DashboardCardBlock(minHeight = 140.px) {
        SpanText(
            text = "Nous n'avons trouvé aucune offre d'emploi.",
            modifier = SpanTextStylecss.toModifier()
                .fontFamily(INTER)
//                .fontSize(if (isMobile) 13.px else 20.px)
                .color(Colors.Black).textAlign(TextAlign.Center)
        )
    }
}

@OptIn(DelicateApi::class)
@Composable
fun ApplicationsBlock(
    onAdd: () -> Unit
) {
    DashboardCardBlock(minHeight = 80.px) {
        Row(
            modifier = Modifier
                .gap(8.px)
                .cursor(Cursor.Pointer)
                .onClick { onAdd() },
            verticalAlignment = Alignment.CenterVertically
        ) {
           /* Image(
                src = "/add_27dp_666666.svg",
                description = "Ajouter",
                modifier = Modifier.size(25.px))*/
            FaPlus(
                modifier = Modifier.size(25.px)
                    .color(Color.rgb(0xb5, 0xb5, 0xb5))
            )
            SpanText(
                text = "Ajouter une candidature",
                modifier = SpanTextStylecss.toModifier()
                    .fontFamily(INTER)
//                    .fontSize(if (isMobile) 13.px else 20.px)
                    .color(Colors.Graytext)
            )
        }
    }
}
